// CreateLogicSection.js
import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { CONTENT_TYPES } from '../constants';

const CreateLogicSection = ({
  formData,
  validationErrors,
  airtableBases,
  airtableTables,
  airtableFields,
  isLoadingBases,
  isLoadingTables,
  isLoadingFields,
  toggleSection,
  updateCreateBase,
  updateCreateTable,
  addCreateField,
  removeCreateField,
  updateOverrideSettings
}) => {
  // New field state
  const [newCreateField, setNewCreateField] = useState({
    id: '',
    name: '',
    content: {
      type: 'static',
      value: ''
    }
  });

  // Create Logic handlers
  const handleCreateBaseChange = (e) => {
    const baseId = e.target.value;
    const baseName = e.target.options[e.target.selectedIndex].text;
    updateCreateBase(baseId, baseName);
  };

  const handleCreateTableChange = (e) => {
    const tableId = e.target.value;
    const tableName = e.target.options[e.target.selectedIndex].text;
    updateCreateTable(tableId, tableName);
  };

  const handleCreateFieldSelect = (e) => {
    const fieldId = e.target.value;
    const fieldName = e.target.options[e.target.selectedIndex].text;
    
    setNewCreateField(prev => ({
      ...prev,
      id: fieldId,
      name: fieldName
    }));
  };

  const handleCreateFieldTypeChange = (e) => {
    const type = e.target.value;
    
    setNewCreateField(prev => ({
      ...prev,
      content: {
        ...prev.content,
        type,
        value: type === 'static' ? prev.content.value : ''
      }
    }));
  };

  const handleCreateFieldValueChange = (e) => {
    const value = e.target.value;
    
    setNewCreateField(prev => ({
      ...prev,
      content: {
        ...prev.content,
        value
      }
    }));
  };

  const handleAddCreateField = () => {
    // Don't add if field is empty
    if (!newCreateField.id) return;

    // Validate value is provided if type requires it (static or linked_record)
    const requiresValue = ['static', 'linked_record'].includes(newCreateField.content.type);
    if (requiresValue && !newCreateField.content.value) return;

    addCreateField({ ...newCreateField });
    
    // Reset new field
    setNewCreateField({
      id: '',
      name: '',
      content: {
        type: 'static',
        value: ''
      }
    });
  };

  // Check if validation logic is enabled, required for validated_record type
  const isValidateLogicEnabled = formData.hasValidateLogic;

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium flex items-center">
          <span className={`mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full ${formData.hasCreateLogic ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-400'}`}>
            <Plus className="h-4 w-4" />
          </span>
          Create Logic
        </h3>
        <div className="relative inline-block w-10 mr-2 align-middle select-none">
          <input
            type="checkbox"
            id="toggle-create"
            className="sr-only"
            checked={formData.hasCreateLogic}
            onChange={toggleSection}
          />
          <label 
            htmlFor="toggle-create"
            className={`block overflow-hidden h-6 rounded-full cursor-pointer ${formData.hasCreateLogic ? 'bg-indigo-500' : 'bg-gray-300'}`}
          >
            <span className={`absolute block h-4 w-4 rounded-full bg-white top-1 transition-transform duration-200 ease-in-out ${formData.hasCreateLogic ? 'right-1 transform translate-x-0' : 'left-1 transform translate-x-0'}`}></span>
          </label>
        </div>
      </div>
      
      {formData.hasCreateLogic && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Configure which records to create in Airtable when a QR code is scanned.
          </p>
          
          {/* Airtable Connection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Airtable Base</label>
              <div className="relative">
                <select
                  value={formData.create_logic.create_base.id}
                  onChange={handleCreateBaseChange}
                  disabled={isLoadingBases}
                  className={`w-full px-3 py-2 border ${validationErrors.createBase ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${isLoadingBases ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <option value="">{isLoadingBases ? 'Loading bases...' : 'Select a base'}</option>
                  {airtableBases.map(base => (
                    <option key={base.id} value={base.id}>{base.name}</option>
                  ))}
                </select>
                {isLoadingBases && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <svg className="animate-spin h-4 w-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
              </div>
              {validationErrors.createBase && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.createBase}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Table</label>
              <div className="relative">
                <select
                  value={formData.create_logic.create_table.id}
                  onChange={handleCreateTableChange}
                  disabled={!formData.create_logic.create_base.id || isLoadingTables}
                  className={`w-full px-3 py-2 border ${validationErrors.createTable ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${(!formData.create_logic.create_base.id || isLoadingTables) ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <option value="">
                    {!formData.create_logic.create_base.id 
                      ? 'Select a base first'
                      : isLoadingTables 
                        ? 'Loading tables...' 
                        : 'Select a table'
                    }
                  </option>
                  {airtableTables.map(table => (
                    <option key={table.id} value={table.id}>{table.name}</option>
                  ))}
                </select>
                {isLoadingTables && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <svg className="animate-spin h-4 w-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
              </div>
              {validationErrors.createTable && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.createTable}</p>
              )}
            </div>
          </div>
          
          {/* Create Fields */}
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Fields to Create</h4>
            <p className="text-sm text-gray-500 mb-4">
              Define which fields to populate in the created record.
            </p>
            
            {validationErrors.createFields && (
              <p className="mb-2 text-sm text-red-500">{validationErrors.createFields}</p>
            )}
            
            {/* Existing fields */}
            <div className="space-y-2 mb-4">
              {formData.create_logic.create_fields.map((field, index) => (
                <div key={index} className="flex items-center bg-gray-50 p-2 rounded">
                  <div className="flex-grow">
                    <span className="text-sm">
                      <strong>{field.name}</strong>: {CONTENT_TYPES.find(type => type.value === field.content.type)?.label || field.content.type}
                      {field.content.type === 'static' && (
                        <> <em>"{field.content.value}"</em></>
                      )}
                      {field.content.type === 'linked_record' && field.content.value && (
                        <> <span className="font-mono text-xs">({field.content.value})</span></>
                      )}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCreateField(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            
            {/* Add new field */}
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-5">
                <select
                  value={newCreateField.id}
                  onChange={handleCreateFieldSelect}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${(!formData.create_logic.create_table.id || isLoadingFields) ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} disabled:opacity-50 disabled:cursor-not-allowed`}
                  disabled={!formData.create_logic.create_table.id || isLoadingFields}
                >
                  <option value="">
                    {!formData.create_logic.create_table.id 
                      ? 'Select a table first'
                      : isLoadingFields 
                        ? 'Loading fields...' 
                        : 'Select field'
                    }
                  </option>
                  {airtableFields.map(field => (
                    <option key={field.id} value={field.id}>{field.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-3">
                <select
                  value={newCreateField.content.type}
                  onChange={handleCreateFieldTypeChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                >
                  {CONTENT_TYPES.map(type => (
                    <option 
                      key={type.value} 
                      value={type.value}
                      disabled={type.value === 'validated_record' && !isValidateLogicEnabled}
                    >
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-3">
                <input
                  type="text"
                  placeholder={
                    newCreateField.content.type === 'static'
                      ? 'Static value'
                      : newCreateField.content.type === 'linked_record'
                      ? 'Record ID (rec...)'
                      : 'N/A'
                  }
                  value={newCreateField.content.value}
                  onChange={handleCreateFieldValueChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={!['static', 'linked_record'].includes(newCreateField.content.type)}
                />
              </div>
              <div className="col-span-1">
                <button
                  type="button"
                  onClick={handleAddCreateField}
                  disabled={
                    !newCreateField.id ||
                    (['static', 'linked_record'].includes(newCreateField.content.type) && !newCreateField.content.value)
                  }
                  className="h-full w-full flex items-center justify-center bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Configure fields to create in the new record. "Scanned Value" will use the QR content.
              {isValidateLogicEnabled ? " \"Validated Record Link\" will connect to the matched record from validation." : " Enable Validate Logic to use \"Validated Record Link\"."}
            </p>
          </div>

          {/* Override Settings */}
          {isValidateLogicEnabled && (
            <div className="mt-6 border-t border-gray-200 pt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Manual Override Settings</h4>
              <p className="text-sm text-gray-500 mb-4">
                Allow users to manually override failed validations and still create records in Airtable.
              </p>

              <div className="space-y-4">
                {/* Allow Override Checkbox */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="allow-override"
                    checked={formData.create_logic.allow_override || false}
                    onChange={(e) => updateOverrideSettings({ allow_override: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="allow-override" className="ml-2 block text-sm text-gray-700">
                    Allow manual override for failed validations
                  </label>
                </div>

                {/* Override Flag Field (only shown when override is allowed) */}
                {formData.create_logic.allow_override && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Override Flag Field (Optional)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Select a boolean/checkbox field to mark records that were created via override
                    </p>
                    <select
                      value={formData.create_logic.override_flag_field?.id || ''}
                      onChange={(e) => {
                        const fieldId = e.target.value;
                        if (fieldId) {
                          const fieldName = e.target.options[e.target.selectedIndex].text;
                          updateOverrideSettings({
                            override_flag_field: { id: fieldId, name: fieldName }
                          });
                        } else {
                          updateOverrideSettings({ override_flag_field: null });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!formData.create_logic.create_table.id || isLoadingFields}
                    >
                      <option value="">No flag field (optional)</option>
                      {airtableFields
                        .filter(field => field.type === 'checkbox' || field.type === 'singleCheckbox')
                        .map(field => (
                          <option key={field.id} value={field.id}>{field.name}</option>
                        ))
                      }
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      This field will be set to "true" when a record is created via override
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {!formData.hasCreateLogic && (
        <p className="text-sm text-gray-500">
          Enable this section to create new records in Airtable when QR codes are scanned.
        </p>
      )}
    </div>
  );
};

export default CreateLogicSection;